import React from "react";
import {
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import * as Sentry from "@sentry/react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(_error) {
    return {hasError: true};
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    try {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        extra: {
          errorInfo: errorInfo,
        },
      });
    } catch (e) {
      console.warn("Failed to capture exception in Sentry:", e);
    }
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
      isRetrying: true,
    }));

    // Reset retrying state after a short delay
    setTimeout(() => {
      this.setState({isRetrying: false});
    }, 1000);
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isLazyLoadingError =
        this.state.error?.message?.includes("Loading chunk") ||
        this.state.error?.message?.includes("Loading CSS chunk") ||
        this.state.error?.name === "ChunkLoadError";

      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center p-8 max-w-lg mx-auto">
            <div className="mb-6">
              <AlertTriangle className="mx-auto h-16 w-16 text-red-500" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {isLazyLoadingError
                ? "Failed to Load Page"
                : "Something went wrong"}
            </h2>

            <p className="text-gray-600 mb-6">
              {isLazyLoadingError
                ? "We encountered an issue while loading this page. This might be due to a network issue or an outdated version."
                : "We're sorry, but something unexpected happened while loading this page."}
            </p>

            {this.state.retryCount > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  Retry attempt: {this.state.retryCount}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                disabled={this.state.isRetrying}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {this.state.isRetrying ? (
                  <>
                    <RefreshCw className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                    Retrying...
                  </>
                ) : (
                  "Try Again"
                )}
              </button>

              <button
                onClick={this.handleReload}
                className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-md hover:bg-gray-300 transition-colors"
              >
                Reload Page
              </button>

              {isLazyLoadingError && (
                <button
                  onClick={() => {
                    // Clear cache and reload
                    if ("caches" in window) {
                      caches.keys().then((names) => {
                        names.forEach((name) => caches.delete(name));
                      });
                    }
                    window.location.reload();
                  }}
                  className="w-full bg-orange-100 text-orange-800 px-6 py-3 rounded-md hover:bg-orange-200 transition-colors"
                >
                  Clear Cache & Reload
                </button>
              )}
            </div>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 font-medium">
                  Error Details (Development)
                </summary>
                <div className="mt-3 p-4 bg-gray-100 rounded-md">
                  <pre className="text-xs text-red-600 overflow-auto whitespace-pre-wrap">
                    <strong>Error:</strong> {this.state.error?.toString()}
                    {this.state.errorInfo?.componentStack && (
                      <>
                        {"\n\n<strong>Component Stack:</strong>\n"}
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
